import { useEffect, useState, useCallback } from "react";
import { api, errText } from "../../lib/api";
import { Button } from "../../components/ui/button";
import { Input } from "../../components/ui/input";
import { Label } from "../../components/ui/label";
import { Textarea } from "../../components/ui/textarea";
import { Switch } from "../../components/ui/switch";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "../../components/ui/select";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription } from "../../components/ui/dialog";
import { Avatar, AvatarFallback, AvatarImage } from "../../components/ui/avatar";
import { CATEGORIES, PROVINCES } from "../../lib/constants";
import { photoUrl } from "../../lib/api";
import { toast } from "sonner";
import { Loader2, UserPlus, ImagePlus, X, Check, Search, BadgeCheck } from "lucide-react";

const SELLER_TYPES = ["Individual", "Breeder", "Business", "Shelter"];
const LISTING_CATEGORIES = CATEGORIES.filter((c) => c.id !== "all");
const LISTING_PROVINCES = PROVINCES.filter((p) => p.code !== "all");
const MAX_PHOTOS = 10;

const EMPTY_USER = {
  name: "", email: "", password: "", username: "", role: "user",
  seller_type: "Individual", phone: "", bio: "", verified_seller: false, verified_business: false,
};

export function CreateUserDialog({ open, onClose, onCreated }) {
  const [form, setForm] = useState(EMPTY_USER);
  const [saving, setSaving] = useState(false);
  const set = (k, v) => setForm((f) => ({ ...f, [k]: v }));

  useEffect(() => { if (open) setForm(EMPTY_USER); }, [open]);

  const save = async () => {
    if (!form.name.trim()) return toast.error("Name is required");
    if (!form.email.trim()) return toast.error("Email is required");
    if (form.password.length < 8) return toast.error("Password must be at least 8 characters");
    setSaving(true);
    try {
      const { data } = await api.post("/admin/users", form);
      toast.success(`Account created for ${data.name}`);
      onCreated?.(data);
      onClose();
    } catch (e) { toast.error(errText(e)); }
    finally { setSaving(false); }
  };

  return (
    <Dialog open={open} onOpenChange={(o) => !o && onClose()}>
      <DialogContent className="sm:max-w-md max-h-[90vh] overflow-y-auto bg-white" data-testid="create-user-dialog">
        <DialogHeader>
          <DialogTitle className="font-display text-[#12234A] flex items-center gap-2"><UserPlus size={18} className="text-[#F5551E]" /> Create user account</DialogTitle>
          <DialogDescription className="text-slate-500">Set a password for the account — you can share it with the user.</DialogDescription>
        </DialogHeader>
        <div className="space-y-3">
          <div><Label>Name</Label><Input value={form.name} onChange={(e) => set("name", e.target.value)} data-testid="create-user-name" className="mt-1 h-10 rounded-xl" placeholder="Full name or business name" /></div>
          <div><Label>Email</Label><Input value={form.email} onChange={(e) => set("email", e.target.value)} data-testid="create-user-email" className="mt-1 h-10 rounded-xl" placeholder="user@example.com" /></div>
          <div><Label>Password</Label><Input value={form.password} onChange={(e) => set("password", e.target.value)} data-testid="create-user-password" className="mt-1 h-10 rounded-xl" placeholder="At least 8 characters" /></div>
          <div><Label>Username <span className="text-slate-400 font-normal">(optional)</span></Label><Input value={form.username} onChange={(e) => set("username", e.target.value)} data-testid="create-user-username" className="mt-1 h-10 rounded-xl" placeholder="public handle" /></div>
          <div className="grid grid-cols-2 gap-3">
            <div>
              <Label>Role</Label>
              <Select value={form.role} onValueChange={(v) => set("role", v)}>
                <SelectTrigger data-testid="create-user-role" className="mt-1 h-10 rounded-xl"><SelectValue /></SelectTrigger>
                <SelectContent className="bg-white">
                  {["user", "moderator", "admin"].map((r) => <SelectItem key={r} value={r} className="capitalize">{r}</SelectItem>)}
                </SelectContent>
              </Select>
            </div>
            <div>
              <Label>Seller type</Label>
              <Select value={form.seller_type} onValueChange={(v) => set("seller_type", v)}>
                <SelectTrigger data-testid="create-user-seller-type" className="mt-1 h-10 rounded-xl"><SelectValue /></SelectTrigger>
                <SelectContent className="bg-white">
                  {SELLER_TYPES.map((t) => <SelectItem key={t} value={t}>{t}</SelectItem>)}
                </SelectContent>
              </Select>
            </div>
          </div>
          <div><Label>Phone <span className="text-slate-400 font-normal">(optional)</span></Label><Input value={form.phone} onChange={(e) => set("phone", e.target.value)} data-testid="create-user-phone" className="mt-1 h-10 rounded-xl" /></div>
          <div><Label>Bio <span className="text-slate-400 font-normal">(optional)</span></Label><Textarea value={form.bio} onChange={(e) => set("bio", e.target.value)} data-testid="create-user-bio" rows={2} className="mt-1 rounded-xl" /></div>
          <div className="flex items-center justify-between py-1">
            <Label>Verified seller</Label>
            <Switch checked={form.verified_seller} onCheckedChange={(v) => set("verified_seller", v)} data-testid="create-user-verified-seller" />
          </div>
          <div className="flex items-center justify-between py-1">
            <Label>Verified business</Label>
            <Switch checked={form.verified_business} onCheckedChange={(v) => set("verified_business", v)} data-testid="create-user-verified-business" />
          </div>
          <Button onClick={save} disabled={saving} data-testid="save-create-user-btn" className="w-full h-11 rounded-xl bg-[#F5551E] hover:bg-[#E04812] text-white font-semibold">
            {saving ? <Loader2 size={16} className="animate-spin" /> : "Create account"}
          </Button>
        </div>
      </DialogContent>
    </Dialog>
  );
}

function SellerPicker({ seller, onPick }) {
  const [q, setQ] = useState("");
  const [results, setResults] = useState([]);
  const [open, setOpen] = useState(false);
  const [loading, setLoading] = useState(false);
  const [newUserOpen, setNewUserOpen] = useState(false);

  const search = useCallback(async (term) => {
    setLoading(true);
    try {
      const { data } = await api.get(`/admin/users?q=${encodeURIComponent(term)}`);
      setResults(data.slice(0, 20));
    } catch (e) { toast.error(errText(e)); }
    finally { setLoading(false); }
  }, []);

  useEffect(() => {
    if (!open) return;
    const t = setTimeout(() => search(q), 250);
    return () => clearTimeout(t);
  }, [q, open, search]);

  if (seller) {
    return (
      <div className="flex items-center gap-3 rounded-xl border border-slate-200 bg-white p-3" data-testid="selected-seller">
        <Avatar className="h-10 w-10"><AvatarImage src={photoUrl(seller.picture)} /><AvatarFallback className="bg-[#12234A] text-white text-xs">{seller.name?.[0]}</AvatarFallback></Avatar>
        <div className="flex-1 min-w-0">
          <p className="font-semibold text-[#12234A] truncate flex items-center gap-1.5">{seller.name}{seller.is_verified_seller && <BadgeCheck size={14} className="text-emerald-500" />}</p>
          <p className="text-xs text-slate-400 truncate">{seller.email}</p>
        </div>
        <Button variant="outline" size="sm" onClick={() => onPick(null)} data-testid="change-seller-btn" className="rounded-lg">Change</Button>
      </div>
    );
  }

  return (
    <div>
      <div className="relative">
        <Search size={16} className="absolute left-3 top-1/2 -translate-y-1/2 text-slate-400" />
        <Input
          value={q}
          onFocus={() => { setOpen(true); if (!results.length) search(""); }}
          onChange={(e) => setQ(e.target.value)}
          data-testid="seller-search"
          placeholder="Search a seller by name or email…"
          className="pl-9 h-10 rounded-xl"
        />
      </div>
      {open && (
        <div className="mt-2 rounded-xl border border-slate-200 bg-white max-h-60 overflow-y-auto" data-testid="seller-results">
          {loading ? (
            <div className="p-4 flex justify-center"><Loader2 size={18} className="animate-spin text-[#F5551E]" /></div>
          ) : results.length === 0 ? (
            <p className="p-4 text-sm text-slate-400 text-center">No users found</p>
          ) : results.map((u) => (
            <button
              key={u.user_id}
              onClick={() => { onPick(u); setOpen(false); }}
              data-testid={`seller-option-${u.user_id}`}
              className="w-full flex items-center gap-3 p-2.5 hover:bg-slate-50 text-left transition-colors"
            >
              <Avatar className="h-8 w-8"><AvatarImage src={photoUrl(u.picture)} /><AvatarFallback className="bg-[#12234A] text-white text-[10px]">{u.name?.[0]}</AvatarFallback></Avatar>
              <div className="flex-1 min-w-0"><p className="text-sm font-medium text-[#12234A] truncate">{u.name}</p><p className="text-xs text-slate-400 truncate">{u.email}</p></div>
            </button>
          ))}
        </div>
      )}
      <Button variant="outline" onClick={() => setNewUserOpen(true)} data-testid="create-new-seller-btn" className="mt-2 w-full rounded-xl gap-1.5 border-dashed"><UserPlus size={15} /> Create a new seller account</Button>
      <CreateUserDialog open={newUserOpen} onClose={() => setNewUserOpen(false)} onCreated={(u) => onPick(u)} />
    </div>
  );
}

const EMPTY_LISTING = {
  title: "", description: "", category: "dogs", species: "", breed: "", age: "",
  price: "", price_type: "fixed", province: "ON", city: "",
  show_email: false, show_phone: false, contact_email: "", contact_phone: "",
};

export function AdminCreateListing() {
  const [seller, setSeller] = useState(null);
  const [form, setForm] = useState(EMPTY_LISTING);
  const [photos, setPhotos] = useState([]);
  const [uploading, setUploading] = useState(false);
  const [saving, setSaving] = useState(false);
  const set = (k, v) => setForm((f) => ({ ...f, [k]: v }));

  const onImage = async (e) => {
    const files = Array.from(e.target.files || []);
    e.target.value = "";
    if (!files.length) return;
    const remaining = MAX_PHOTOS - photos.length;
    if (remaining <= 0) return toast.error(`Up to ${MAX_PHOTOS} photos per ad`);
    setUploading(true);
    try {
      for (const file of files.slice(0, remaining)) {
        if (!file.type.startsWith("image/")) { toast.error("Please choose image files only"); continue; }
        const fd = new FormData();
        fd.append("file", file);
        const { data } = await api.post("/upload", fd, { headers: { "Content-Type": "multipart/form-data" } });
        setPhotos((prev) => [...prev, data.path]);
      }
    } catch (err) { toast.error(errText(err)); }
    finally { setUploading(false); }
  };

  const submit = async () => {
    if (!seller) return toast.error("Choose or create a seller for this listing");
    if (!form.title.trim()) return toast.error("Title is required");
    if (!form.city.trim()) return toast.error("City is required");
    setSaving(true);
    try {
      const payload = {
        ...form,
        user_id: seller.user_id,
        photos,
        price: form.price_type === "fixed" && form.price !== "" ? Number(form.price) : null,
      };
      await api.post("/admin/listings", payload);
      toast.success("Listing created and published");
      setForm(EMPTY_LISTING);
      setPhotos([]);
      setSeller(null);
    } catch (e) { toast.error(errText(e)); }
    finally { setSaving(false); }
  };

  return (
    <div className="max-w-2xl space-y-5" data-testid="admin-create-listing">
      <div>
        <Label className="text-[#12234A] font-semibold">Seller</Label>
        <p className="text-xs text-slate-400 mb-2">This listing will be posted under this account.</p>
        <SellerPicker seller={seller} onPick={setSeller} />
      </div>

      <div className="rounded-2xl border border-slate-200 bg-white p-5 space-y-4">
        <div><Label>Title</Label><Input value={form.title} onChange={(e) => set("title", e.target.value)} data-testid="listing-title" className="mt-1 h-10 rounded-xl" placeholder="e.g. Golden Retriever Puppies" /></div>

        <div className="grid grid-cols-2 gap-3">
          <div>
            <Label>Category</Label>
            <Select value={form.category} onValueChange={(v) => set("category", v)}>
              <SelectTrigger data-testid="listing-category" className="mt-1 h-10 rounded-xl"><SelectValue /></SelectTrigger>
              <SelectContent className="bg-white">{LISTING_CATEGORIES.map((c) => <SelectItem key={c.id} value={c.id}>{c.label}</SelectItem>)}</SelectContent>
            </Select>
          </div>
          <div>
            <Label>Price type</Label>
            <Select value={form.price_type} onValueChange={(v) => set("price_type", v)}>
              <SelectTrigger data-testid="listing-price-type" className="mt-1 h-10 rounded-xl"><SelectValue /></SelectTrigger>
              <SelectContent className="bg-white">
                <SelectItem value="fixed">Fixed price</SelectItem>
                <SelectItem value="free">Free</SelectItem>
                <SelectItem value="contact">Contact for price</SelectItem>
              </SelectContent>
            </Select>
          </div>
        </div>

        {form.price_type === "fixed" && (
          <div><Label>Price (CAD)</Label><Input type="number" value={form.price} onChange={(e) => set("price", e.target.value)} data-testid="listing-price" className="mt-1 h-10 rounded-xl" placeholder="0" /></div>
        )}

        <div className="grid grid-cols-3 gap-3">
          <div><Label>Species</Label><Input value={form.species} onChange={(e) => set("species", e.target.value)} data-testid="listing-species" className="mt-1 h-10 rounded-xl" placeholder="Dog" /></div>
          <div><Label>Breed</Label><Input value={form.breed} onChange={(e) => set("breed", e.target.value)} data-testid="listing-breed" className="mt-1 h-10 rounded-xl" placeholder="Golden Retriever" /></div>
          <div><Label>Age</Label><Input value={form.age} onChange={(e) => set("age", e.target.value)} data-testid="listing-age" className="mt-1 h-10 rounded-xl" placeholder="8 weeks" /></div>
        </div>

        <div className="grid grid-cols-2 gap-3">
          <div>
            <Label>Province</Label>
            <Select value={form.province} onValueChange={(v) => set("province", v)}>
              <SelectTrigger data-testid="listing-province" className="mt-1 h-10 rounded-xl"><SelectValue /></SelectTrigger>
              <SelectContent className="bg-white">{LISTING_PROVINCES.map((p) => <SelectItem key={p.code} value={p.code}>{p.name}</SelectItem>)}</SelectContent>
            </Select>
          </div>
          <div><Label>City</Label><Input value={form.city} onChange={(e) => set("city", e.target.value)} data-testid="listing-city" className="mt-1 h-10 rounded-xl" placeholder="Toronto" /></div>
        </div>

        <div><Label>Description</Label><Textarea value={form.description} onChange={(e) => set("description", e.target.value)} data-testid="listing-description" rows={4} className="mt-1 rounded-xl" placeholder="Describe the pet, supply or service…" /></div>

        <div>
          <Label>Photos <span className="text-slate-400 font-normal">(up to {MAX_PHOTOS})</span></Label>
          <div className="mt-2 flex flex-wrap gap-2">
            {photos.map((p, ix) => (
              <div key={p} className="relative h-20 w-20 rounded-xl overflow-hidden border border-slate-200">
                <img src={photoUrl(p)} alt="" className="h-full w-full object-cover" />
                <button type="button" onClick={() => setPhotos((prev) => prev.filter((_, i) => i !== ix))} data-testid={`listing-photo-remove-${ix}`} className="absolute -top-1.5 -right-1.5 h-6 w-6 rounded-full bg-black/70 text-white flex items-center justify-center hover:bg-black"><X size={13} /></button>
              </div>
            ))}
            {photos.length < MAX_PHOTOS && (
              <label data-testid="listing-photo-upload" className="h-20 w-20 rounded-xl border-2 border-dashed border-slate-300 flex items-center justify-center cursor-pointer hover:border-[#F5551E] text-slate-400 hover:text-[#F5551E] transition-colors">
                {uploading ? <Loader2 size={20} className="animate-spin" /> : <ImagePlus size={20} />}
                <input type="file" accept="image/*" multiple onChange={onImage} className="hidden" />
              </label>
            )}
          </div>
        </div>

        <div className="flex items-center justify-between py-1"><Label>Show email on listing</Label><Switch checked={form.show_email} onCheckedChange={(v) => set("show_email", v)} data-testid="listing-show-email" /></div>
        <div className="flex items-center justify-between py-1"><Label>Show phone on listing</Label><Switch checked={form.show_phone} onCheckedChange={(v) => set("show_phone", v)} data-testid="listing-show-phone" /></div>
        <div className="grid grid-cols-2 gap-3">
          <div><Label>Contact email <span className="text-slate-400 font-normal">(optional)</span></Label><Input value={form.contact_email} onChange={(e) => set("contact_email", e.target.value)} data-testid="listing-contact-email" className="mt-1 h-10 rounded-xl" /></div>
          <div><Label>Contact phone <span className="text-slate-400 font-normal">(optional)</span></Label><Input value={form.contact_phone} onChange={(e) => set("contact_phone", e.target.value)} data-testid="listing-contact-phone" className="mt-1 h-10 rounded-xl" /></div>
        </div>
      </div>

      <Button onClick={submit} disabled={saving} data-testid="submit-listing-btn" className="w-full h-12 rounded-xl bg-[#F5551E] hover:bg-[#E04812] text-white font-semibold gap-2">
        {saving ? <Loader2 size={18} className="animate-spin" /> : <><Check size={18} /> Publish listing</>}
      </Button>
      <p className="text-xs text-slate-400 text-center">Admin-created listings go live immediately.</p>
    </div>
  );
}
